<template>
  <section class="mt-10 bg-white border border-gray-200 rounded-xl shadow-sm">
    <div class="px-5 py-4 border-b border-gray-100">
      <h3 class="font-semibold text-[#22282F]">Visibilité des types de document</h3>
      <p class="text-sm text-gray-500 mt-1">
        Choisissez qui voit les types de document <strong>globaux</strong> (non rattachés à une entité)
        lors de la rédaction d'une note ou d'une réponse à un courrier. Les types propres à l'entité de
        l'utilisateur restent toujours visibles.
      </p>
    </div>

    <div v-if="loading" class="flex justify-center py-10">
      <div class="w-6 h-6 border-4 border-gray-200 border-t-[#22282F] rounded-full animate-spin" />
    </div>

    <div v-else class="p-5 space-y-6">
      <!-- Règle générale par portée -->
      <div>
        <h4 class="text-sm font-semibold text-gray-700 mb-2">Règle générale</h4>
        <div class="divide-y divide-gray-100 border border-gray-200 rounded-lg">
          <label
            v-for="p in portees"
            :key="p.code"
            class="flex items-center justify-between gap-4 px-4 py-3 cursor-pointer"
          >
            <span class="text-sm text-gray-700">{{ p.label }}</span>
            <span class="flex items-center gap-2 shrink-0">
              <span class="text-xs" :class="defaults[p.code] ? 'text-green-600' : 'text-gray-500'">
                {{ defaults[p.code] ? 'Voit les types globaux' : 'Ne voit pas les types globaux' }}
              </span>
              <input v-model="defaults[p.code]" type="checkbox" class="w-4 h-4 accent-[#22282F]" />
            </span>
          </label>
        </div>
      </div>

      <!-- Exceptions -->
      <div>
        <h4 class="text-sm font-semibold text-gray-700 mb-1">Exceptions</h4>
        <p class="text-xs text-gray-500 mb-2">Une exception pour une ambassade ou une direction prime sur la règle générale.</p>

        <div v-if="exceptions.length" class="space-y-2 mb-3">
          <div
            v-for="(exc, i) in exceptions"
            :key="exc.key"
            class="flex items-center gap-3 border border-gray-200 rounded-lg px-4 py-2"
          >
            <span class="text-sm text-gray-700 flex-1 min-w-0 truncate">
              <span class="text-xs text-gray-400 mr-1">{{ exc.kind === 'ambassade' ? 'Ambassade' : 'Direction' }}</span>
              {{ exc.nom }}
            </span>
            <label class="flex items-center gap-2 text-xs text-gray-600 shrink-0">
              <input v-model="exc.voit_globaux" type="checkbox" class="w-4 h-4 accent-[#22282F]" />
              Voit les types globaux
            </label>
            <button
              type="button"
              class="text-gray-400 hover:text-red-600 text-sm px-1"
              title="Retirer l'exception"
              @click="exceptions.splice(i, 1)"
            >✕</button>
          </div>
        </div>
        <p v-else class="text-xs text-gray-400 italic mb-3">Aucune exception.</p>

        <div class="flex flex-col sm:flex-row gap-2">
          <select
            v-model="newEntity"
            class="flex-1 px-3 py-2 border border-gray-200 rounded-lg text-sm bg-white focus:outline-none focus:border-[#22282F]"
          >
            <option value="">Ajouter une exception pour…</option>
            <optgroup label="Ambassades">
              <option v-for="a in availableAmbassades" :key="'a' + a.id" :value="'a:' + a.id">{{ a.nom }}</option>
            </optgroup>
            <optgroup label="Directions">
              <option v-for="d in availableDirections" :key="'d' + d.id" :value="'d:' + d.id">{{ d.nom }}</option>
            </optgroup>
          </select>
          <button
            type="button"
            :disabled="!newEntity"
            class="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 disabled:opacity-40"
            @click="addException"
          >Ajouter</button>
        </div>
      </div>

      <div class="flex justify-end">
        <button
          type="button"
          :disabled="saving"
          class="px-4 py-2 bg-[#22282F] text-white rounded-lg text-sm hover:bg-[#22282F]/90 disabled:opacity-50"
          @click="save"
        >{{ saving ? 'Enregistrement…' : 'Enregistrer' }}</button>
      </div>
    </div>
  </section>
</template>

<script setup lang="ts">
import { ref, reactive, computed, onMounted } from 'vue'
import { apiFetch } from '@/utils/apiFetch'
import { useToast } from '@/utils/toast'

interface Entity { id: number; nom: string }
interface ApiException {
  portee: string
  ambassade_id: number | null
  direction_id: number | null
  nom: string
  voit_globaux: boolean
}
interface ApiPayload {
  defaults: Record<string, boolean>
  exceptions: ApiException[]
}
interface ExceptionRow { key: string; kind: 'ambassade' | 'direction'; id: number; nom: string; voit_globaux: boolean }

const toast = useToast()

const portees = [
  { code: 'AMBASSADE', label: 'Utilisateurs rattachés à une ambassade' },
  { code: 'DIRECTION', label: 'Utilisateurs rattachés à une direction' },
  { code: 'NATIONAL', label: 'Utilisateurs sans rattachement (national)' },
]

const loading = ref(true)
const saving = ref(false)
const defaults = reactive<Record<string, boolean>>({ AMBASSADE: true, DIRECTION: true, NATIONAL: true })
const exceptions = ref<ExceptionRow[]>([])
const ambassades = ref<Entity[]>([])
const directions = ref<Entity[]>([])
const newEntity = ref('')

const isTaken = (kind: 'ambassade' | 'direction', id: number) =>
  exceptions.value.some(e => e.kind === kind && e.id === id)
const availableAmbassades = computed(() => ambassades.value.filter(a => !isTaken('ambassade', a.id)))
const availableDirections = computed(() => directions.value.filter(d => !isTaken('direction', d.id)))

const toRows = (list: ApiException[]): ExceptionRow[] =>
  list.map(e => e.ambassade_id
    ? { key: `a${e.ambassade_id}`, kind: 'ambassade', id: e.ambassade_id, nom: e.nom, voit_globaux: e.voit_globaux }
    : { key: `d${e.direction_id}`, kind: 'direction', id: e.direction_id as number, nom: e.nom, voit_globaux: e.voit_globaux })

const apply = (payload: ApiPayload) => {
  Object.assign(defaults, payload.defaults)
  exceptions.value = toRows(payload.exceptions)
}

const addException = () => {
  const [kind, rawId] = newEntity.value.split(':')
  const id = Number(rawId)
  const list = kind === 'a' ? ambassades.value : directions.value
  const entity = list.find(e => e.id === id)
  if (!entity) return
  // Par défaut, l'exception inverse la règle générale de sa portée.
  const portee = kind === 'a' ? 'AMBASSADE' : 'DIRECTION'
  exceptions.value.push({
    key: `${kind}${id}`,
    kind: kind === 'a' ? 'ambassade' : 'direction',
    id,
    nom: entity.nom,
    voit_globaux: !defaults[portee],
  })
  newEntity.value = ''
}

const save = async () => {
  saving.value = true
  try {
    const payload = await apiFetch<ApiPayload>('/api/admin/document-types/visibility/', {
      method: 'PUT',
      body: JSON.stringify({
        defaults,
        exceptions: exceptions.value.map(e => ({
          ...(e.kind === 'ambassade' ? { ambassade_id: e.id } : { direction_id: e.id }),
          voit_globaux: e.voit_globaux,
        })),
      }),
    })
    apply(payload)
    toast.success('Visibilité enregistrée', 'La règle s\'applique dès la prochaine rédaction')
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible d\'enregistrer la visibilité')
  } finally {
    saving.value = false
  }
}

onMounted(async () => {
  try {
    const [payload, amb, dirs] = await Promise.all([
      apiFetch<ApiPayload>('/api/admin/document-types/visibility/'),
      apiFetch<Entity[]>('/api/admin/ambassades/'),
      apiFetch<Entity[]>('/api/admin/directions/'),
    ])
    ambassades.value = amb
    directions.value = dirs
    apply(payload)
  } catch (e: any) {
    toast.error('Erreur', e.message || 'Impossible de charger la visibilité des types de document')
  } finally {
    loading.value = false
  }
})
</script>
